import {
  faChevronDown,
  faChevronUp,
  faX
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from 'components/Flex';
import { P } from 'components/Typography/Typography';
import { DefaultTFuncReturn, t } from 'i18next';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import { Button } from '../Button';
import { FieldLabel } from '../FieldLabel';
import type { IOption } from './nestedSelectTypes';
import { Option } from './Option';
import { parseNestedSelectSelectedLabels } from './parseNestedSelectSelectedLabels';
import { flattenOptions } from './utilities';
import { ErrorText } from '../ErrorText';
import ClickOutside from 'components/click-outside';

export type { IOption } from './nestedSelectTypes';
const Wrapper = styled(Flex)<{ error: Props['error']; isOpen: boolean }>`
  width: 100%;
  min-height: 35px;
  height: ${({ isOpen }) => (isOpen ? '35px' : 'auto')};
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.palette.neutral.lightGrey};
  justify-content: space-between;
  position: relative;

  ${({ error, theme }) =>
    error
      ? `
      border: 1px solid ${theme.palette.accent.red};
      box-shadow: 0 0 0 3px ${theme.palette.accent.lightRed};
    `
      : ''}
`;

/** Lista wg viewportu (`fixed`) — omija clip overflow modali; może wystawać poza kartę. */
const DropdownPanel = styled(Flex)`
  position: fixed;
  box-sizing: border-box;
  z-index: 10050;
  flex-direction: column;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.palette.neutral.white};
  border: 1px solid ${({ theme }) => theme.palette.neutral.light};
  box-shadow: 2px 2px 10px 0px ${({ theme }) => theme.palette.neutral.lightGrey};

  scrollbar-width: thin;
`;

const SelectedTitle = styled(Flex)<{ disabled?: boolean }>`
  background-color: ${({ theme, disabled }) =>
    disabled
      ? theme.palette.neutral.lightGrey
      : theme.palette.accent.veryLightBlue};
  color: ${({ theme, disabled }) =>
    disabled ? theme.palette.neutral.grey : theme.palette.accent.blue};
  align-items: center;
  height: 24px;
  margin-bottom: 4px;
`;

const HelperText = styled(P)`
  text-align: right;
  font-style: oblique;
  width: 100%;
`;

const SelectedContainer = styled(Flex)<{ isOpen: boolean }>`
  width: 100%;
  padding: ${({ isOpen }) => (isOpen ? '0 0 0 8px' : '4px 0 0 8px')};
  align-items: ${({ isOpen }) => (isOpen ? 'center' : 'flex-start')};
  overflow-x: hidden;
  overflow-y: auto;
  flex-wrap: ${({ isOpen }) => (isOpen ? 'nowrap' : 'wrap')};
  max-height: ${({ isOpen }) => (isOpen ? '32px' : 'none')};
`;

interface Props {
  label?: string | null;
  disabled?: boolean;
  helperText?: string;
  selectedOptions: IOption[];
  setSelectedOptions: (options: IOption[]) => void;
  options: IOption[];
  isOneOptionSelect?: boolean;
  /** Z isOneOptionSelect: ten sam drzewiasty UI co multi, ale jeden wybór — dowolny węzeł. */
  singleSelectAnyNode?: boolean;
  width?: string | string[];
  error?: string | DefaultTFuncReturn;
}

export const NestedSelect = ({
  selectedOptions,
  setSelectedOptions,
  label,
  helperText,
  disabled,
  options,
  isOneOptionSelect,
  singleSelectAnyNode,
  width,
  error
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  type MenuGeom = {
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  };
  const [menuGeom, setMenuGeom] = useState<MenuGeom | null>(null);

  const updateMenuGeom = useCallback(() => {
    if (!isOpen || typeof window === 'undefined') return;
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gutter = 12;
    const vhBudget = Math.min(window.innerHeight * 0.78, 840);
    const spaceBelow = window.innerHeight - rect.bottom - gutter;
    const clampedBelow = Math.max(Math.min(spaceBelow, vhBudget), 140);
    const left = Math.max(gutter, Math.min(rect.left, window.innerWidth - rect.width - gutter));
    const width = Math.min(rect.width, window.innerWidth - left - gutter);
    setMenuGeom({
      top: rect.bottom + 4,
      left,
      width,
      maxHeight: clampedBelow
    });
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuGeom();
  }, [isOpen, options, updateMenuGeom]);

  useEffect(() => {
    if (!isOpen) return;
    const onReflow = () => updateMenuGeom();
    window.addEventListener('resize', onReflow);
    document.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      document.removeEventListener('scroll', onReflow, true);
    };
  }, [isOpen, updateMenuGeom]);

  useEffect(() => {
    if (!isOpen) setMenuGeom(null);
  }, [isOpen]);

  const displayedLabels = useMemo(
    () => parseNestedSelectSelectedLabels(options, selectedOptions),
    [options, selectedOptions]
  );

  const handleRemoveSpecificOption = (optionLabel: string) => {
    const foundItem = flattenOptions(options, false).find(
      (item) => (item.selectedLabel ?? item.label) === optionLabel
    );

    if (foundItem) {
      const foundOptions = flattenOptions(foundItem?.options || [foundItem]);

      const newSelectedOptions = selectedOptions.filter(
        (selectedOptionFilter) =>
          !foundOptions.find(
            (selectedOption) =>
              selectedOption.value === selectedOptionFilter.value
          )
      );

      setSelectedOptions(newSelectedOptions);
    }
  };

  return (
    <ClickOutside active={isOpen} onClick={() => setIsOpen(false)}>
      <Flex flexDirection="column" width={width || '100%'}>
        <Flex justifyContent="space-between">
          {label && <FieldLabel>{t(label)}</FieldLabel>}
          {helperText && (
            <HelperText variant="body">{t(helperText)}</HelperText>
          )}
        </Flex>

        <Wrapper ref={anchorRef} error={error} isOpen={isOpen}>
          <SelectedContainer isOpen={isOpen}>
            {displayedLabels.length
              ? displayedLabels.map((selectedOption: string) => (
                  <SelectedTitle
                    mr={1}
                    p={1}
                    key={selectedOption}
                    disabled={disabled}
                  >
                    {selectedOption}
                    {!disabled && (
                      <Button
                        minHeight="18px"
                        variant="lightBlue"
                        icon={<FontAwesomeIcon icon={faX} />}
                        onClick={() => {
                          handleRemoveSpecificOption(selectedOption);
                        }}
                      />
                    )}
                  </SelectedTitle>
                ))
              : ''}
          </SelectedContainer>
          <Flex>
            {!disabled && (
              <Button
                minHeight="32px"
                variant="secondary"
                icon={<FontAwesomeIcon color="grey" icon={faX} />}
                onClick={() => {
                  setSelectedOptions([]);
                }}
              />
            )}

            <Button
              minHeight="32px"
              variant="secondary"
              icon={
                <FontAwesomeIcon
                  color="grey"
                  icon={isOpen ? faChevronUp : faChevronDown}
                />
              }
              onClick={() => {
                setIsOpen((prevIsOpen) => !prevIsOpen);
              }}
            />
          </Flex>

          {isOpen && menuGeom && (
            <DropdownPanel
              style={{
                top: menuGeom.top,
                left: menuGeom.left,
                width: menuGeom.width,
                maxHeight: menuGeom.maxHeight
              }}
              role="listbox"
            >
              {options.map((option) => (
                <Option
                  disabled={disabled}
                  key={option.label}
                  option={option}
                  selectedOptions={selectedOptions}
                  setSelectedOptions={setSelectedOptions}
                  isOneOptionSelect={isOneOptionSelect}
                  singleSelectAnyNode={singleSelectAnyNode}
                  setIsSelectOpen={setIsOpen}
                />
              ))}
            </DropdownPanel>
          )}
        </Wrapper>
        {error && <ErrorText>{error}</ErrorText>}
      </Flex>
    </ClickOutside>
  );
};
